import { CircleAlert, Download, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api-error';
import { toastApiError } from '@/lib/toast';

type LineError = { line: number; message: string };

/**
 * Baixar o modelo, importar a planilha e mostrar os erros por linha (o servidor grava tudo ou
 * nada). Usado pelo faturamento do perfil fiscal e pelos custos do catálogo.
 */
export function SpreadsheetImport({
  label,
  hint,
  downloadLabel = 'Baixar modelo',
  pending,
  onDownload,
  onImport,
}: {
  /** Nome da planilha, para o leitor de tela ("Planilha de faturamento"). */
  label: string;
  hint?: string;
  downloadLabel?: string;
  pending: boolean;
  onDownload: () => Promise<void>;
  /** Importa; erro com `details.errors` vira a lista de linhas. */
  onImport: (file: File) => Promise<void>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<LineError[]>([]);

  async function handleFile(file: File) {
    setErrors([]);
    try {
      await onImport(file);
    } catch (caught) {
      const details =
        caught instanceof ApiError
          ? (caught.details as { errors?: LineError[] } | undefined)
          : undefined;
      if (details?.errors) setErrors(details.errors);
      else toastApiError(caught, 'Não foi possível importar a planilha.');
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => void onDownload().catch((error: unknown) => toastApiError(error))}
        >
          <Download aria-hidden /> {downloadLabel}
        </Button>
        <Button onClick={() => fileInput.current?.click()} disabled={pending}>
          <Upload aria-hidden /> {pending ? 'Importando…' : 'Importar planilha'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.csv"
          className="sr-only"
          aria-label={label}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        {hint && <p className="w-full text-xs text-muted-foreground">{hint}</p>}
      </div>
      {errors.length > 0 && (
        <Alert variant="destructive">
          <CircleAlert aria-hidden />
          <AlertTitle>Nada foi importado. Corrija estas linhas:</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-5">
              {errors.map((error) => (
                <li key={`${error.line}-${error.message}`}>
                  linha {error.line}: {error.message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
